import React, { useContext, useEffect, useRef, useState } from 'react';
import { GlobalContext } from '../../contexts/GlobalContext';
import axios from '../../utils/axiosConfig';

const CompanySearchModal = ({ isOpen, onClose, onSelectCompany, currentCompany }) => {
  const { userProfile } = useContext(GlobalContext);
  const [companies, setCompanies] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newCompany, setNewCompany] = useState({
    company_name: '',
    logo_url: '',
    linkedin_id: '',
  });
  const [createLoading, setCreateLoading] = useState(false);
  const searchInputRef = useRef(null);

  // Fetch companies when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchCompanies();
      setSearchQuery('');
      setShowCreateForm(false);
      setError(null);
      // Focus search input when modal opens
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    }
  }, [isOpen]);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/v1/company/all');
      if (response.data && response.data.companies) {
        setCompanies(response.data.companies);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      setError('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort companies - prioritize exact matches
  const filteredCompanies = companies
    .filter((company) => company.company_name.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      const aExact = a.company_name.toLowerCase() === searchQuery.toLowerCase();
      const bExact = b.company_name.toLowerCase() === searchQuery.toLowerCase();
      const aStarts = a.company_name.toLowerCase().startsWith(searchQuery.toLowerCase());
      const bStarts = b.company_name.toLowerCase().startsWith(searchQuery.toLowerCase());

      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;
      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;

      return a.company_name.localeCompare(b.company_name);
    });

  // Company logo logic borrowed from JobBoard
  const getCompanyLogo = (company) => {
    if (company.logo_url) {
      return company.logo_url;
    }

    // Generate logo based on company name using ui-avatars
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(company.company_name)}&background=6366f1&color=fff&bold=true`;
  };

  const handleCompanySelect = (company) => {
    onSelectCompany({
      ...company,
      isExisting: true,
    });
    onClose();
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    if (!newCompany.company_name.trim()) return;

    setCreateLoading(true);
    try {
      const response = await axios.post('/api/v1/company/create', {
        company: {
          company_name: newCompany.company_name,
          logo_url: newCompany.logo_url,
          linkedin_id: newCompany.linkedin_id,
        },
      });

      if (response.data && response.data.company_id) {
        const createdCompany = {
          company_id: response.data.company_id,
          company_name: newCompany.company_name,
          logo_url: newCompany.logo_url,
          linkedin_id: newCompany.linkedin_id,
          is_vetted: false,
          is_public: false,
        };
        setCompanies((prev) => [createdCompany, ...prev]);
        onSelectCompany({
          company_id: createdCompany.company_id,
          company_name: createdCompany.company_name,
          logo_url: createdCompany.logo_url,
          linkedin_id: createdCompany.linkedin_id,
          isExisting: true,
        });
        onClose();
        setShowCreateForm(false);
        setNewCompany({
          company_name: '',
          logo_url: '',
          linkedin_id: '',
        });
      }
    } catch (error) {
      console.error('Error creating company:', error);
      if (error.response && error.response.data && error.response.data.detail) {
        setError(error.response.data.detail);
      } else {
        setError('Failed to create company');
      }
    } finally {
      setCreateLoading(false);
    }
  };

  const handleShowCreateForm = () => {
    setShowCreateForm(true);
    setNewCompany({
      company_name: searchQuery,
      logo_url: '',
      linkedin_id: '',
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-9999 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div
          className="fixed inset-0 bg-gray-500/75 transition-opacity"
          onClick={onClose}
        ></div>

        <div className="relative transform overflow-hidden rounded-xl bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl">
          {/* Header */}
          <div className="px-4 py-6 sm:px-6 lg:px-8 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-800">
                  {showCreateForm ? 'Create New Company' : 'Select Company'}
                </h1>
                <p className="mt-1 text-sm text-gray-600">
                  {showCreateForm
                    ? 'Add a new company to the database'
                    : 'Choose an existing company or create a new one'}
                </p>
              </div>
              <button
                type="button"
                className="inline-flex justify-center items-center size-8 text-sm font-semibold rounded-lg border border-transparent text-gray-800 hover:bg-gray-100 disabled:opacity-50 disabled:pointer-events-none"
                onClick={onClose}
              >
                <span className="sr-only">Close</span>
                <svg
                  className="shrink-0 size-4"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m18 6-12 12" />
                  <path d="m6 6 12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="px-4 py-6 sm:px-6 lg:px-8">
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
                <div className="flex">
                  <div className="shrink-0">
                    <svg
                      className="size-5 text-red-400"
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="15" y1="9" x2="9" y2="15" />
                      <line x1="9" y1="9" x2="15" y2="15" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">Error</h3>
                    <p className="mt-1 text-sm text-red-700">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {!showCreateForm ? (
              <div className="space-y-6">
                {/* Search Input */}
                <div>
                  <label
                    htmlFor="company-search"
                    className="block mb-2 text-sm font-medium text-gray-700"
                  >
                    Search Companies
                  </label>
                  <div className="relative">
                    <input
                      id="company-search"
                      ref={searchInputRef}
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Type company name to search..."
                      className="py-2.5 sm:py-3 px-4 pe-11 block w-full border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
                    />
                    <div className="absolute inset-y-0 inset-e-0 flex items-center pointer-events-none pe-4">
                      <svg
                        className="size-4 text-gray-400"
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="11" cy="11" r="8" />
                        <path d="m21 21-4.35-4.35" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Company List */}
                <div className="max-h-96 overflow-y-auto">
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  ) : (
                    <>
                      {filteredCompanies.length > 0 ? (
                        <div className="grid gap-3">
                          {filteredCompanies.map((company) => (
                            <button
                              key={company.company_id}
                              onClick={() => handleCompanySelect(company)}
                              className="group relative p-4 text-left hover:bg-gray-50 border border-gray-200 rounded-lg flex items-center space-x-4 transition-all duration-200 hover:border-blue-300 focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <div className="shrink-0">
                                <img
                                  src={getCompanyLogo(company)}
                                  alt={`${company.company_name} logo`}
                                  className="size-12 object-contain bg-gray-100 rounded-lg ring-2 ring-gray-200 group-hover:ring-blue-300 transition-all duration-200"
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                                  {company.company_name}
                                </h3>
                                <div className="mt-1 flex items-center space-x-2">
                                  <span
                                    className={`inline-flex items-center px-2 py-1 rounded-sm text-xs font-medium ${
                                      company.is_public
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-gray-100 text-gray-800'
                                    }`}
                                  >
                                    {company.is_public ? 'Public' : 'Private'}
                                  </span>
                                  <span
                                    className={`inline-flex items-center px-2 py-1 rounded-sm text-xs font-medium ${
                                      company.is_vetted
                                        ? 'bg-blue-100 text-blue-800'
                                        : 'bg-yellow-100 text-yellow-800'
                                    }`}
                                  >
                                    {company.is_vetted ? 'Vetted' : 'Unvetted'}
                                  </span>
                                </div>
                              </div>
                              <div className="shrink-0">
                                <svg
                                  className="size-5 text-gray-400 group-hover:text-blue-500 transition-colors"
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="m9 18 6-6-6-6" />
                                </svg>
                              </div>
                            </button>
                          ))}
                        </div>
                      ) : searchQuery.length > 0 ? (
                        <div className="text-center py-12">
                          <svg
                            className="mx-auto size-12 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            ></path>
                          </svg>
                          <h3 className="mt-2 text-sm font-medium text-gray-900">
                            No companies found
                          </h3>
                          <p className="mt-1 text-sm text-gray-500">
                            No companies match "{searchQuery}". Try a different search term.
                          </p>
                        </div>
                      ) : (
                        <div className="text-center py-12">
                          <svg
                            className="mx-auto size-12 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                            ></path>
                          </svg>
                          <h3 className="mt-2 text-sm font-medium text-gray-900">
                            Start searching
                          </h3>
                          <p className="mt-1 text-sm text-gray-500">
                            Type a company name to search existing companies.
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Create New Company Button */}
                {searchQuery.length > 0 && (
                  <div className="border-t border-gray-200 pt-6">
                    <button
                      onClick={handleShowCreateForm}
                      className="w-full py-3 px-4 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-lg border-2 border-dashed border-gray-300 text-gray-600 hover:border-blue-400 hover:text-blue-600 focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                    >
                      <svg
                        className="size-4"
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M5 12h14" />
                        <path d="M12 5v14" />
                      </svg>
                      Create "{searchQuery}" as new company
                    </button>
                  </div>
                )}
              </div>
            ) : (
              /* Create Company Form */
              <form onSubmit={handleCreateCompany} className="space-y-6">
                <div className="grid gap-4 lg:gap-6">
                  <div>
                    <label
                      htmlFor="company-name"
                      className="block mb-2 text-sm font-medium text-gray-700"
                    >
                      Company Name *
                    </label>
                    <input
                      type="text"
                      id="company-name"
                      value={newCompany.company_name}
                      onChange={(e) =>
                        setNewCompany((prev) => ({ ...prev, company_name: e.target.value }))
                      }
                      className="py-2.5 sm:py-3 px-4 block w-full border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
                      placeholder="Enter company name"
                      required
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="logo-url"
                      className="block mb-2 text-sm font-medium text-gray-700"
                    >
                      Logo URL (optional)
                    </label>
                    <input
                      type="url"
                      id="logo-url"
                      value={newCompany.logo_url}
                      onChange={(e) =>
                        setNewCompany((prev) => ({ ...prev, logo_url: e.target.value }))
                      }
                      className="py-2.5 sm:py-3 px-4 block w-full border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
                      placeholder="https://example.com/logo.png"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="linkedin-id"
                      className="block mb-2 text-sm font-medium text-gray-700"
                    >
                      LinkedIn Company ID (optional)
                    </label>
                    <input
                      type="text"
                      id="linkedin-id"
                      value={newCompany.linkedin_id}
                      onChange={(e) =>
                        setNewCompany((prev) => ({ ...prev, linkedin_id: e.target.value }))
                      }
                      className="py-2.5 sm:py-3 px-4 block w-full border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none"
                      placeholder="company-name"
                    />
                  </div>
                </div>
              </form>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-4 sm:px-6 lg:px-8 border-t border-gray-200 bg-gray-50">
            <div className="flex justify-end gap-x-3">
              {showCreateForm ? (
                <>
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    className="py-2.5 px-4 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-lg border border-gray-200 bg-white text-gray-800 shadow-xs hover:bg-gray-50 focus:outline-hidden focus:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none"
                  >
                    <svg
                      className="size-4"
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="m15 18-6-6 6-6" />
                    </svg>
                    Back to Search
                  </button>
                  <button
                    type="submit"
                    onClick={handleCreateCompany}
                    disabled={createLoading || !newCompany.company_name.trim()}
                    className="py-2.5 px-4 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-lg border border-transparent bg-blue-600 text-white hover:bg-blue-700 focus:outline-hidden focus:bg-blue-700 disabled:opacity-50 disabled:pointer-events-none"
                  >
                    {createLoading && (
                      <div
                        className="animate-spin inline-block size-4 border-[3px] border-current border-t-transparent text-white rounded-full"
                        role="status"
                        aria-label="loading"
                      >
                        <span className="sr-only">Loading...</span>
                      </div>
                    )}
                    {createLoading ? 'Creating...' : 'Create Company'}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={onClose}
                  className="py-2.5 px-4 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-lg border border-gray-200 bg-white text-gray-800 shadow-xs hover:bg-gray-50 focus:outline-hidden focus:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanySearchModal;
